import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, FormControlLabel, Pagination, Switch, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { AppNotification } from '../../interfaces/notification.interface';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import ScrollableTable from '../common/ScrollableTable';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { isSafeDeepLink, notifyNotificationsChanged, severityColor, sourceLabel } from './notificationText';

const th = { padding: '12px', textAlign: 'left' as const, backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' };
const td = { padding: '12px', borderBottom: '1px solid #ddd', verticalAlign: 'top' as const };
const PAGE_SIZE = 20;
const SEVERITIES = [
    { id: 'INFO', label: 'Info' },
    { id: 'WARNING', label: 'Warning' },
    { id: 'OVERDUE', label: 'Overdue' },
];

const NotificationsPage: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [rows, setRows] = useState<AppNotification[]>([]);
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [severity, setSeverity] = useState('');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    /** With "Unread only" on: how many unread match vs how many match without the filter. */
    const [counts, setCounts] = useState<{ unread: number; all: number } | null>(null);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            // The unfiltered total is fetched first so the list call stays the last one made.
            const allTotal = unreadOnly
                ? NotificationService.list(institutionId, { unreadOnly: false, severity, page: 0, size: 1 }).then(r => r.totalElements)
                : Promise.resolve(null);
            const res = await NotificationService.list(institutionId, { unreadOnly, severity, page, size: PAGE_SIZE });
            setRows(res.content);
            setTotalPages(res.totalPages);
            const all = await allTotal.catch(() => null);
            setCounts(unreadOnly && all != null && res.totalElements != null ? { unread: res.totalElements, all } : null);
        } catch (err) {
            toast.showApiError(err, 'Failed to load notifications');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unreadOnly, severity, page]);

    useEffect(() => { load(); }, [load]);

    const markRead = async (n: AppNotification) => {
        try {
            await NotificationService.markRead(n.id);
            setRows(list => list.map(r => (r.id === n.id ? { ...r, readAt: new Date().toISOString() } : r)));
            notifyNotificationsChanged();
        } catch (err) {
            toast.showApiError(err, 'Failed to mark as read');
        }
    };

    const markAll = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            await NotificationService.markAllRead(institutionId);
            toast.showSuccess('All notifications marked as read');
            notifyNotificationsChanged();
            load();
        } catch (err) {
            toast.showApiError(err, 'Failed to mark all as read');
        }
    };

    const open = async (n: AppNotification) => {
        if (!n.readAt) await markRead(n);
        if (isSafeDeepLink(n.deepLink)) navigate(n.deepLink);
    };

    return (
        <div style={{ padding: '20px' }} data-help-page="notifications.list.page">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Notifications</h2>
                <Button variant="outlined" onClick={markAll} data-help="notifications.list.markAll">Mark all read</Button>
            </div>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }} data-help-section="notifications.list.filters">
                <Box sx={{ flex: '1 1 220px', maxWidth: 280 }} data-help="notifications.list.severity">
                    <SearchableSelect
                        options={SEVERITIES}
                        value={severity}
                        onChange={id => { setSeverity(id); setPage(0); }}
                        getOptionId={o => o.id}
                        getOptionLabel={o => o.label}
                        label="Severity"
                        size="small"
                        clearable
                    />
                </Box>
                <Box sx={{ flex: '1 1 180px', maxWidth: 240 }} data-help="notifications.list.unreadOnly">
                    <FormControlLabel
                        control={<Switch checked={unreadOnly} onChange={e => { setUnreadOnly(e.target.checked); setPage(0); }} />}
                        label="Unread only"
                    />
                </Box>
            </Box>
            {!loading && counts && counts.all > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="unread-summary">
                    {counts.unread === counts.all
                        ? `All ${counts.all} notification${counts.all === 1 ? ' is' : 's are'} unread, so this list is the same as without the filter.`
                        : `Showing ${counts.unread} unread of ${counts.all} notification${counts.all === 1 ? '' : 's'}.`}
                </Typography>
            )}
            {loading ? (
                <Typography>Loading...</Typography>
            ) : rows.length === 0 ? (
                <Typography color="text.secondary">No notifications.</Typography>
            ) : (
                <ScrollableTable>
                    <thead>
                        <tr>
                            <th style={th} data-help="notifications.list.col.notification">Notification</th>
                            <th style={th} data-help="notifications.list.col.source">Source</th>
                            <th style={th} data-help="notifications.list.col.due">Due</th>
                            <th style={th} data-help="notifications.list.col.received">Received</th>
                            <th style={th} data-help="notifications.list.col.actions"></th>
                        </tr>
                    </thead>
                    <tbody data-help-section="notifications.list.table">
                        {rows.map(n => (
                            <tr key={n.id} style={{ backgroundColor: n.readAt ? undefined : '#f3f8ff' }}>
                                <td style={td}>
                                    <div style={{ fontWeight: n.readAt ? 400 : 600 }}>{n.title}</div>
                                    {n.body && <div style={{ color: '#555', fontSize: 13 }}>{n.body}</div>}
                                </td>
                                <td style={td}>
                                    {sourceLabel(n.sourceKey)}{' '}
                                    {n.severity !== 'INFO' && <Chip size="small" label={n.severity} color={severityColor(n.severity)} />}
                                </td>
                                <td style={td}>{n.dueDate ? formatDate(n.dueDate) : '-'}</td>
                                <td style={td}>{formatDateTime(n.createdAt)}</td>
                                <td style={td}>
                                    {isSafeDeepLink(n.deepLink) && <Button size="small" onClick={() => open(n)} data-help="notifications.list.action.open">Open</Button>}
                                    {!n.readAt && <Button size="small" onClick={() => markRead(n)} data-help="notifications.list.action.markRead">Mark read</Button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </ScrollableTable>
            )}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination data-help="notifications.list.paging" count={totalPages} page={page + 1} onChange={(_, p) => setPage(p - 1)} />
                </Box>
            )}
        </div>
    );
};

export default NotificationsPage;
